const GA_MEASUREMENT_ID = import.meta.env.VITE_PUBLIC_GA_MEASUREMENT_ID as string | undefined;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let loaded = false;

// Carga el script de Google Analytics (gtag.js) en runtime, SOLO cuando se llama explicitamente
// -- nunca se auto-carga en index.html. Este producto es para ninos, asi que se respeta la misma
// postura de privacidad que el resto del sitio: nada de tracking hasta que el usuario acepte el
// banner de cookies (ver CookieConsent.tsx, que es el unico lugar que llama a esta funcion).
export function loadGoogleAnalytics() {
  if (loaded || !GA_MEASUREMENT_ID || typeof document === 'undefined') return;
  loaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  window.gtag('js', new Date());
  // Causa raiz real del "0 usuarios en Realtime/DebugView" (21/09/2026): Google Consent Mode.
  // gtag.js asume analytics_storage:'denied' por defecto a menos que se le diga lo contrario
  // explicitamente -- y sin esta llamada, el contenedor GTM se inicializaba, procesaba la cola
  // de eventos, pero nunca mandaba ningun hit de red ni seteaba la cookie _ga, exactamente lo
  // que se observo (confirmado inspeccionando window.google_tag_manager directamente). Como
  // esta funcion SOLO se llama despues de que el usuario acepto el banner de cookies del sitio
  // (CookieConsent.tsx), el consentimiento real ya existe en ese punto -- se lo comunicamos a
  // gtag explicitamente en vez de dejar que asuma el default 'denied'.
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  // send_page_view en false: las vistas de pagina se envian a mano en cada cambio de ruta
  // (ver trackPageView / App.tsx), porque en una SPA gtag('config') solo dispara la primera
  // automaticamente y no se entera de la navegacion por React Router.
  // DIAGNOSTICO TEMPORAL (21/09/2026): debug_mode:true hace que los eventos aparezcan de
  // inmediato en GA4 DebugView (Admin -> DebugView), que es mas confiable que Realtime para
  // confirmar si los hits realmente estan llegando a Google. Quitar una vez confirmado.
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false, debug_mode: true });

  // Bug real encontrado en produccion (21/09/2026): AnalyticsRouteTracker (App.tsx) solo
  // dispara trackPageView() cuando cambia la URL -- pero aceptar el banner de cookies NO
  // cambia la URL, asi que la primera pagina (donde el usuario justo dio consentimiento)
  // nunca mandaba su page_view. GA quedaba "cargado" pero sin ningun hit real llegando a
  // Google (Realtime mostraba 0 usuarios). Se envia la vista actual aqui mismo, apenas GA
  // termina de cargar, sin depender de un cambio de ruta posterior.
  trackPageView(window.location.pathname + window.location.search);
}

export function trackPageView(path: string) {
  if (!loaded || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
  });
}

export function isAnalyticsLoaded() {
  return loaded;
}
